import React, { FC } from 'react';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import { Flex, Grid } from '$components/layouts';
import { useTranslation } from '$hooks';
import { formatPrice } from '$lib/helpers';
import { Splash } from '$components/elements/product-image/components/splash';
import { B2CDeliveryMethod } from '$constants';
import { IOrderLineModel } from '$models';
import { AppLinkHandler } from '~/modules/app';
import { Text } from '$components/elements/text';
import { Image } from '$components/elements/image';
import { ErrorText } from '$components/elements/error-text';

type ReceiptLineItemProps = { line: IOrderLineModel };

export const ReceiptLineItem: FC<ReceiptLineItemProps> = ({ line }): JSX.Element => {
    const { translate } = useTranslation();
    const theme = useTheme();

    return (
        <ReceiptLineWrapper flow="row" columnGap="extrasmall" columns="1fr 10% 20% 10% 10%">
            <ContentCell>
                <LineImageWrapper>
                    {!!line.primaryImageUrl && (
                        <AppLinkHandler href={line.productUrl}>
                            <Image height={60} width={80} src={line.primaryImageUrl} />
                            {!!line?.itemPriceTagUrl && (
                                <SplashContainer>
                                    <Splash src={line.itemPriceTagUrl ?? ''} />
                                </SplashContainer>
                            )}
                        </AppLinkHandler>
                    )}
                </LineImageWrapper>
                <Flex column>
                    {!line.description && line?.product?.b2CDeliveryMethod !== B2CDeliveryMethod.Download && (
                        <Text noSpacing size={theme.fontSizes.xs}>
                            {line?.product?.authors?.toString() || line?.product?.itemGroupName}
                        </Text>
                    )}
                    <AppLinkHandler href={line.productUrl}>
                        <Text
                            noSpacing
                            size={theme.fontSizes.sm}
                            font="heading"
                            fontWeight="Bold"
                            color={theme.colors.black}
                        >
                            {line?.product?.name}
                        </Text>
                    </AppLinkHandler>
                    {!!line.description && (
                        <Text size={theme.fontSizes.xs} color={theme.colors.primaryGrey}>
                            {line.description}
                        </Text>
                    )}
                    {line.itemPriceTagValue && <Text size={theme.fontSizes.xs}>{line.itemPriceTagValue}</Text>}
                    {(line?.voucherDiscount || 0) > 0 && (
                        <Text size={theme.fontSizes.sm} color={theme.colors.primaryGrey}>{`${translate(
                            'checkout.orderConfirmation.voucherLabel'
                        )} ${line.voucher}`}</Text>
                    )}
                    {(line?.mixAndMatchCampaignDiscount || 0) > 0 && line.mixAndMatchCampaignName !== null && (
                        <Text size={theme.fontSizes.xs}>{`${translate('checkout.orderConfirmation.mixMatchLabel')} ${
                            line.mixAndMatchCampaignName
                        }`}</Text>
                    )}
                    {line.hasErrors && <ErrorText>{line.errorMessage}</ErrorText>}
                </Flex>
            </ContentCell>
            <ContentCell justifyContent="center">{line.quantity}</ContentCell>
            <ContentCell>{formatPrice(line.lineSinglePrice)}</ContentCell>
            <ContentCell>
                {!!line.lineDiscount && <Text color={theme.colors.errorTxt}>{formatPrice(line.lineDiscount)}</Text>}
            </ContentCell>
            <ContentCell justifyContent={'flex-end'}>
                <Text fontWeight="Bold">{formatPrice(line.lineTotalPrice)}</Text>
            </ContentCell>
        </ReceiptLineWrapper>
    );
};

const ReceiptLineWrapper = styled(Grid)(({ theme }) => ({
    borderBottom: theme.general.border,
}));

const LineImageWrapper = styled.div(({ theme }) => ({
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    width: '80px',
    height: '60px',
    marginRight: theme.space[3],
}));

const SplashContainer = styled.div({
    position: 'absolute',
    bottom: 0,
    right: 0,
    width: '55%',
    height: 'auto',
});

const ContentCell = styled(Flex)(({ theme }) => ({
    alignItems: 'center',
    padding: `${theme.space[3]} ${theme.space[2]}`,
}));
